import React, { cloneElement, useCallback } from 'react';
import { Button, makeStyles } from '@material-ui/core';
import { Dialog } from 'components/common';
import SearchInput from 'components/list/SearchInput';
import { Filters } from 'icons';
import { debounce, isEqual, omit, set } from 'lodash';
import { useDispatch } from 'react-redux';
import { Field, Form } from 'react-final-form';
import { setFilters as actionSetFilters } from 'store/actions/filtersActions';
import FilterChips from './FilterChips';

const useStyles = makeStyles((theme) => ({
  button: {
    color: 'white',
    '& button': {
      color: 'inherit',
    },
  },
  form: {
    width: '100%',
    paddingRight: theme.spacing(),
    '& .hide-filter, & .MuiInput-input, & .MuiFormLabel-root, & .MuiSelect-icon': {
      color: 'white',
    },
    '& .MuiSelect-select': {
      minWidth: 50,
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: 'white',
    },
    '& .MuiChip-label': {
      color: theme.palette.primary.main,
    },
    '& .MuiChip-avatar': {
      width: 24,
      height: 24,
    },
  },
  dialogRoot: {
    display: 'flex',
    flexDirection: 'column',
  },
  resetButton: {
    fontSize: 12,
  },
  filterChips: {
    margin: 0,
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  searchInput: {
    minWidth: '300px',
  },
}));

const DialogTitle = ({ onFilterReset }) => {
  const classes = useStyles();

  return (
    <>
      Filters
      <Button color="primary" onClick={onFilterReset} className={classes.resetButton}>
        Reset
      </Button>
    </>
  );
};

const FilterButton = (props) => (
  <Button variant="contained" {...props}>
    <Filters />
  </Button>
);

const Filter = (props) => {
  const dispatch = useDispatch();
  const {
    resource,
    children,
    filterValues,
    initialFilters,
    setFilters,
    basePath,
    btnProps = {},
    classes: classesOverride = {},
    withSearch,
    enableLiveSearch = true,
    filterDefaultValues = {},
    syncWithStore = true,
    loading,
    searchKey = 'q',
    autoSelectSearchOnFocus = false,
    clearableSearch = false,
  } = props;

  const classes = { ...useStyles(), ...classesOverride };
  const [open, setOpen] = React.useState(false);
  const handleClickOpen = useCallback(() => setOpen(true), [setOpen]);
  const handleSubmitClose = useCallback(() => setOpen(false), [setOpen]);
  const filters = React.Children.toArray(children);

  const handleClose = (form) => () => {
    setOpen(false);
    form.reset();
  };

  const mergedFilterValues = { ...filterDefaultValues, ...filterValues };
  const mergedInitialValuesWithDefaultValues = mergeInitialValuesWithDefaultValues({
    initialValues: mergedFilterValues,
    filters,
  });

  const buttonProps = { onClick: handleClickOpen, ...btnProps };

  const normalizeSearchValues = useCallback(
    (values) => {
      if (!clearableSearch || values[searchKey]) return values;

      return omit(values, [searchKey]);
    },
    [clearableSearch, searchKey]
  );

  const applyFilters = useCallback(
    (values) => {
      const normalizedValues = normalizeSearchValues(values);

      setFilters(normalizedValues);
      if (syncWithStore) {
        dispatch(
          actionSetFilters({
            resource,
            filters: normalizedValues,
          })
        );
      }
      handleSubmitClose();
    },
    [dispatch, handleSubmitClose, normalizeSearchValues, resource, setFilters, syncWithStore]
  );

  const debouncedSubmit = React.useMemo(
    () =>
      debounce((submitCallback) => {
        submitCallback();
      }, 1000),
    []
  );

  const onFilterReset = (form) => {
    const resetFilterValues = initialFilters || filterDefaultValues;
    const resetFormValues = mergeInitialValuesWithDefaultValues({
      initialValues: { ...filterDefaultValues, ...resetFilterValues },
      filters,
    });

    debouncedSubmit.cancel();
    form.reset(resetFormValues);
    dispatch(
      actionSetFilters({
        resource,
        filters: { ...resetFilterValues },
      })
    );
    setFilters(resetFilterValues);
  };

  React.useEffect(() => {
    return () => {
      debouncedSubmit.cancel();
    };
  }, [debouncedSubmit]);

  const onLiveSearch = useCallback(
    (submitCallback) => {
      debouncedSubmit(submitCallback);
    },
    [debouncedSubmit]
  );

  const onSearchClear = useCallback(
    (form) => {
      debouncedSubmit.cancel();
      form.change(searchKey, undefined);
      applyFilters(omit(form.getState().values, [searchKey]));
    },
    [applyFilters, debouncedSubmit, searchKey]
  );

  return (
    <Form
      onSubmit={applyFilters}
      initialValues={mergedInitialValuesWithDefaultValues}
      initialValuesEqual={isEqual}
      render={({ handleSubmit, form }) => {
        return (
          <form onSubmit={handleSubmit} className={classes.form}>
            {withSearch && (
              <Field
                key={searchKey}
                name={searchKey}
                label="Search"
                filterButton={<FilterButton {...buttonProps} />}
                render={(inputProps) => (
                  <SearchInput
                    classes={{
                      root: classes.searchInput,
                    }}
                    {...inputProps}
                    {...{
                      filterValues: mergedFilterValues,
                      filters,
                      setFilters,
                      resource,
                    }}
                    loading={loading}
                    autoSelectOnFocus={autoSelectSearchOnFocus}
                    clearable={clearableSearch}
                    onSearchChange={() => (enableLiveSearch ? onLiveSearch(handleSubmit) : null)}
                    onSearchClear={() => onSearchClear(form)}
                  />
                )}
              />
            )}
            {!withSearch && (
              <div className={classes.filterContainer}>
                <FilterButton {...buttonProps} />
                <FilterChips
                  filterValues={mergedFilterValues}
                  classesOverride={classes.filterChips}
                  filters={filters}
                  setFilters={setFilters}
                  resource={resource}
                />
              </div>
            )}
            <Dialog
              open={open}
              title={<DialogTitle onFilterReset={() => onFilterReset(form)} />}
              confirmText="Apply"
              onCancel={handleClose(form)}
              onConfirm={handleSubmit}>
              <div className={classes.dialogRoot}>
                {filters.map((filter) => (
                  <Field
                    key={filter.props.source}
                    name={filter.props.source}
                    label={filter.props.label}
                    render={(inputProps) =>
                      cloneElement(filter, {
                        ...inputProps,
                        basePath,
                        resource,
                        record: {},
                      })
                    }
                  />
                ))}
              </div>
            </Dialog>
          </form>
        );
      }}
    />
  );
};

const mergeInitialValuesWithDefaultValues = ({ initialValues, filters }) => ({
  ...filters
    .filter((filterElement) => filterElement.props.alwaysOn && filterElement.props.defaultValue)
    .reduce(
      (acc, filterElement) =>
        set({ ...acc }, filterElement.props.source, filterElement.props.defaultValue),
      {}
    ),
  ...initialValues,
});

const getFilterIfNoButton = (props) => (props.context === 'button' ? null : <Filter {...props} />);

export default getFilterIfNoButton;
